import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FaPen, FaTrash, FaEye } from "react-icons/fa";
import { IoSearch } from "react-icons/io5";
import { MdOutlineRestorePage } from "react-icons/md";
import { toast } from "react-toastify";
import Pagination from "UpdatedComponents/Pagination";
import { Puff } from "react-loader-spinner";
import RestoreModal from "UpdatedFeatures/RestoreModal";
import Badge from "UpdatedFeatures/Badge";
import AddTransferModal from "./AddTransfer";
import TrashedSwitch from "UpdatedComponents/TrashedSwitch";
import StatusFilter from "UpdatedFeatures/StatusFilter";
import { deleteTransfer, getTransfers, restoreTransfert } from "UpdatedFeatures/api";
import TransferValidationModal from "./TransferValidationModal";
import EditTransferModal from "./EditTransferModal";
import DeleteTransferModal from "./DeleteTransferModal";
import ViewTransferDetailsModal from "./ViewTransferDetailsModal";
import { useGetAllProductQuery } from "redux/api/product/product";

const formatDate = (timestamp) => {
  return new Date(timestamp).toLocaleDateString("fr-FR");
};

export default function TransferTable() {
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [itemToRestore, setItemToRestore] = useState(null);
  const [isOpenAddTransferModal, setIsOpenAddTransferModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState({
    id: null,
    label: "Tous",
  });
  const [creationDate, setCreationDate] = useState("");
  const [validationModalOpen, setValidationModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransferForEdit, setSelectedTransferForEdit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTransferForDelete, setSelectedTransferForDelete] =
    useState(null);
  const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState(false);
  const [selectedTransferForView, setSelectedTransferForView] = useState(null);
  const queryClient = useQueryClient();
  const { refetch } = useGetAllProductQuery();

  useEffect(() => {
    refetch();
  }, []);

  const STATUS = {
    Pending: 0,
    Active: 1,
    Approved: 2,
    All: null,
  };

  const statusOptions = [
    { id: STATUS.Pending, label: "En cours de transfert" },
    { id: STATUS.Approved, label: "Partiellement transféré" },
    { id: STATUS.Active, label: "Transféré" },
    { id: STATUS.All, label: "Tous" },
  ];

  // Fetch transfers data
  const {
    data: transfersData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      "transfers",
      page,
      perPage,
      search,
      isArchived,
      selectedStatus.id,
      creationDate,
    ],
    queryFn: () =>
      getTransfers(
        page,
        perPage,
        search || "",
        isArchived,
        selectedStatus.id,
        creationDate
      ),
  });

  console.log(transfersData)
  const { mutate: deleteTransferMutation, isLoading: isDeleting } = useMutation(
    {
      mutationFn: deleteTransfer,
      onSuccess: () => {
        toast.success("Arrivage supprimé avec succès");
        queryClient.invalidateQueries(["transfers"]);
        setDeleteModalOpen(false);
      },
      onError: (error) => {
        toast.error(error.message || "Erreur lors de la suppression");
      },
    }
  );
  const { mutate: restoreTransferMutation, isLoading: isRestoring } = useMutation({
    mutationFn: restoreTransfert,
    onSuccess: () => {
      toast.success("Transfert restauré avec succès");
      queryClient.invalidateQueries(["transfers"]);
      setRestoreModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la restauration");
    },
  });

  const handleDeleteConfirm = () => {
    if (selectedTransferForDelete) {
      deleteTransferMutation(selectedTransferForDelete.id);
    }
  };

  const totalPages = Math.ceil((transfersData?.total || 0) / perPage);
  const handleDeleteClick = (Transfer) => {
    if (Transfer.status != 1) {
      return toast.warn(
        "Cet Transfer n'est pas completement Validé, vous pouvez pas le Supprimer!"
      );
    }
    setSelectedTransferForDelete(Transfer);
    setDeleteModalOpen(true);
  };

  const handleSearchChange = (e) => setSearch(e.target.value);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(search);
  };

  const handleRestoreClick = (item) => {
    setItemToRestore(item);
    setRestoreModalOpen(true);
  };

  const handleRestoreConfirm = () => {
    if (itemToRestore) {
      restoreTransferMutation(itemToRestore.id);
    }
  };

  const handleNextPage = () => {
    setPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handlePrevPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleValidateTransfer = (transfer) => {
    setSelectedTransfer(transfer);
    setValidationModalOpen(true);
  };

  const handleEditTransfer = (transfer) => {
    setSelectedTransferForEdit(transfer);
    setEditModalOpen(true);
  };

  const handleViewDetails = (transfer) => {
    setSelectedTransferForView(transfer);
    setViewDetailsModalOpen(true);
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <span className="text-red-600 font-medium text-lg">
          Une erreur s'est produite lors du chargement des données
        </span>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#b269ae] text-white rounded-md hover:bg-[#9c58a3] transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-12">
      <AddTransferModal
        open={isOpenAddTransferModal}
        setOpen={setIsOpenAddTransferModal}
      />

      <div className="sm:flex sm:items-center justify-between space-y-3">
        <div className="relative w-1/3">
          <form onSubmit={handleSearchSubmit} className="flex items-center">
            <input
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={handleSearchChange}
              className="block w-full p-3 pl-20 text-sm border border-[#b269ae] rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-10 ">
              <div className="bg-gray-300 rounded-md px-1">
                <span className="text-xs text-white">TRF</span>
              </div>
            </div>
            <button
              type="submit"
              className="absolute inset-y-0 left-0 flex items-center pl-3"
            >
              <IoSearch color="purple" />
            </button>
          </form>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-center">
            <input
              type="date"
              id="creationDate"
              value={creationDate}
              onChange={(e) => setCreationDate(e.target.value)}
              className="border border-[#b269ae] rounded-md p-2"
              placeholder="Date de création"
            />
          </div>

          <StatusFilter
            statusOptions={statusOptions}
            selected={selectedStatus}
            setSelected={setSelectedStatus}
            STATUS={STATUS}
          />

          <TrashedSwitch enabled={isArchived} setEnabled={setIsArchived} />

          <div className="mt-4 sm:mt-0 flex items-center gap-3">
            <button
              onClick={() => setIsOpenAddTransferModal(true)}
              className="flex items-center justify-center px-3 py-2 rounded-full bg-[#b269ae] text-white font-semibold transition-all duration-300 hover:bg-yellow-400"
            >
              <span className="text-sm">+ Créer un transfert</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <Puff
                    visible={true}
                    height="80"
                    width="80"
                    color="#895a7c"
                    ariaLabel="puff-loading"
                  />
                </div>
              ) : !transfersData?.data?.length ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <span className="text-gray-500 font-medium text-lg">
                    Aucun transfert trouvé
                  </span>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-[#b269ae]">
                    <tr>
                      <th className="py-3.5 pl-4 pr-3 text-left text-sm font-normal text-gray-100 sm:pl-6">
                        Référence
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">
                        Provenance
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">
                        Destination
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">
                        Statut
                      </th>
                      <th className="px-3 py-3.5 text-left text-sm font-normal text-gray-100">
                        Date de création
                      </th>
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {transfersData.data.map((item) => (
                      <tr key={item.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {item.reference || `TRF${item.id}`}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {item?.sender_pos
                            ? item.sender_pos.name
                            : item?.sender_pos_name
                              ? item.sender_pos_name
                              : "KD Marché"}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {[
                            ...new Set(
                              item.transfert_receivers?.map((r) =>
                                r.receiver_pos == null && r.receiver_pos_name == null
                                  ? "KD Marché"
                                  : r.receiver_pos_name
                              )
                            ),
                          ].join(", ")}
                        </td>

                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          <Badge status={item.status} type="transfer" />
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 flex items-center justify-end gap-1">
                          {isArchived ? (
                            <button
                              className="text-[#b269ae] hover:text-purple-300"
                              onClick={() => handleRestoreClick(item)}
                            >
                              <MdOutlineRestorePage
                                size={30}
                                className="text-[#b369ae] hover:cursor-pointer"
                              />
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleViewDetails(item)}
                                className="text-[#b269ae] hover:text-purple-300 mr-3"
                                title="Voir les détails"
                              >
                                <FaEye size={22} />
                              </button>
                              <button
                                onClick={() => handleValidateTransfer(item)}
                                className="flex items-center justify-center px-3 py-1 rounded-full bg-[#b269ae]/20 border border-[#b269ae] text-[#b269ae] font-semibold transition-all duration-300 mr-3 disabled:cursor-not-allowed disabled:bg-gray-600/10 disabled:text-gray-600/40 disabled:border-gray-600/40"
                                disabled={item.status == STATUS.Active}
                              >
                                <span className="text-sm">Valider</span>
                              </button>
                              <button
                                className="text-[#b269ae] hover:text-purple-300"
                                onClick={() => handleEditTransfer(item)}
                                disabled={item.status === STATUS.Active}
                              >
                                <FaPen
                                  className={
                                    item.status === STATUS.Active
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }
                                />
                              </button>
                              <button
                                className="text-[#b269ae] hover:text-purple-300 ml-4"
                                onClick={() => {
                                  handleDeleteClick(item);
                                }}
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <Pagination
        prevPage={handlePrevPage}
        nextPage={handleNextPage}
        totalPages={totalPages}
        setPage={setPage}
      />

      <RestoreModal
        open={restoreModalOpen}
        onConfirm={handleRestoreConfirm}
        onCancel={() => setRestoreModalOpen(false)}
        itemTitle={
          itemToRestore?.reference || `Transfert #${itemToRestore?.id}`
        }
      />

      <TransferValidationModal
        open={validationModalOpen}
        setOpen={setValidationModalOpen}
        selectedTransfer={selectedTransfer}
      />

      <DeleteTransferModal
        open={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        isLoading={isDeleting}
      />
      <EditTransferModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
        selectedTransfer={selectedTransferForEdit}
      />

      <ViewTransferDetailsModal
        open={viewDetailsModalOpen}
        setOpen={setViewDetailsModalOpen}
        selectedTransfer={selectedTransferForView}
      />
    </div>
  );
}
